import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Drawer } from '@/components/ui/Overlay';
import { Toaster } from '@/components/ui/Toaster';
import { PageLoader } from '@/components/ui/States';
import { cn } from '@/lib/cn';
import { PlatformHeader } from '@/features/platform/PlatformHeader';
import { PlatformIdentity, PlatformSidebar, PlatformSidebarNav } from '@/features/platform/PlatformSidebar';

const KEY = 'mce.platform.sidebar';

/** Super Admin shell. Structurally identical to AdminLayout (fixed/collapsible sidebar, drawer below lg) —
    a distinct component because its nav/session/branding are platform-wide, not a single tenant's. */
export function PlatformLayout() {
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } });
  const [drawer, setDrawer] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { window.scrollTo(0, 0); setDrawer(false); }, [pathname]);
  const toggle = () => setCollapsed((c) => { const n = !c; try { localStorage.setItem(KEY, n ? '1' : '0'); } catch { /* ignore */ } return n; });

  return (
    <div className="min-h-dvh bg-background">
      <a href="#platform-main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-secondary focus:px-4 focus:py-2 focus:text-white">Skip to content</a>
      <PlatformSidebar collapsed={collapsed} />
      <div className={cn('transition-[padding] duration-200', collapsed ? 'lg:pl-[4.5rem]' : 'lg:pl-64')}>
        <PlatformHeader onOpenMenu={() => setDrawer(true)} onToggleSidebar={toggle} />
        <main id="platform-main" className="mx-auto w-full max-w-[1440px] p-4 sm:p-6 lg:p-8">
          <Suspense fallback={<PageLoader />}><Outlet /></Suspense>
        </main>
      </div>
      <Drawer open={drawer} onClose={() => setDrawer(false)} side="left" title="Navigation">
        <div className="flex h-full flex-col">
          <div className="border-b border-border px-5 py-3"><PlatformIdentity /></div>
          <PlatformSidebarNav onNavigate={() => setDrawer(false)} />
        </div>
      </Drawer>
      <Toaster />
    </div>
  );
}
